import { MultilingualField } from "@/components/form-items/MultilingualField";
import { CroppedImageInput } from "@/components/form-fields/CroppedImageInput";
import { MediaPreview } from "@/components/media/MediaPreview";
import { Form } from "@/components/ui/form";
import { constructLocaleDict, getMainLanguage } from "@/i18n";
import { sectionImageCaptionSchema, Selection } from "@/lib/cms/section-types";
import { Attachment } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ImportImageForm } from "./SectionForm";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useEditSelection, useImportImage } from "@/api/section";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth/AuthContext";
import { Trash2 } from "lucide-react";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { generateImageName } from "@/lib/utils";
import { useState } from "react";

const formSchema = z.object({
  caption: sectionImageCaptionSchema,
});

type FormData = z.infer<typeof formSchema>;

type ImportImageFormProps = {
  sectionId: string | undefined;
  sectionName: string;
  selection?: Selection;
  onDelete?: () => void;
  onSuccess?: () => void;
};

export const SectionImportImageForm = ({
  sectionId,
  sectionName,
  selection,
  onDelete,
  onSuccess,
}: ImportImageFormProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const { mutateAsync: importImage } = useImportImage(sectionId);
  const { mutateAsync: editSelection } = useEditSelection(sectionId);

  const [pendingMedia, setPendingMedia] = useState<Attachment | undefined>();

  const defaultValues: FormData = {
    caption: constructLocaleDict((lang) => {
      return {
        content: selection?.caption?.[lang]?.content ?? "",
      };
    }),
  };

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const existingAttachment =
    selection?.item && "url" in selection.item
      ? ({
          id: selection.item.id,
          mediumPublicUrl: selection.item.url,
        } as Attachment)
      : undefined;

  // La face d'une œuvre/multiple peut être une vidéo ou un embed : on expose
  // les champs média (parité `SelectionItemMedia`) pour que l'aperçu rende le
  // player. Sans ça, seule `url` (le poster) est passée et on ne voit que la
  // vignette. Attachment dédié à l'aperçu pour ne pas impacter le
  // `CroppedImageInput` (qui se base sur `kind`).
  const itemMedia = selection?.item as
    | (Selection["item"] & {
        kind?: string;
        videoPublicUrl?: string | null;
        posterPublicUrl?: string | null;
        embedProvider?: string;
        embedVideoId?: string;
        embedUrl?: string;
        videoLoop?: boolean;
      })
    | undefined;

  const previewAttachment = existingAttachment
    ? ({
        ...existingAttachment,
        kind: itemMedia?.kind,
        videoPublicUrl: itemMedia?.videoPublicUrl,
        posterPublicUrl: itemMedia?.posterPublicUrl,
        embedProvider: itemMedia?.embedProvider,
        embedVideoId: itemMedia?.embedVideoId,
        embedUrl: itemMedia?.embedUrl,
        videoLoop: itemMedia?.videoLoop,
      } as Attachment)
    : undefined;

  // Média animé (vidéo importée ou embed) : libellé de suppression dédié.
  const isVideoMedia =
    itemMedia?.kind === "video" || itemMedia?.kind === "embed";

  const displayAttachment = pendingMedia ?? existingAttachment;
  const displayPreviewUrl =
    pendingMedia?.mediumPublicUrl ?? selection?.item?.url;

  const onSubmit = async (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    const mediaId = pendingMedia?.id ?? selection?.item?.id;

    if (!mediaId) {
      toast({
        title: t("error"),
        description: t(
          "cms.selection.section.importImageForm.error.imageRequired",
        ),
        variant: "error",
        duration: 1000,
      });
      return;
    }

    if (selection?.item.url && !pendingMedia) {
      await editSelection(
        {
          importDto: {
            imageId: mediaId,
            caption: submittedData.caption,
          },
        },
        {
          onError: (error) => {
            console.error("Error", error);
            toast({
              title: t("error"),
              description: t(
                "cms.selection.section.importImageForm.error.importFailed",
              ),
              variant: "error",
              duration: 1000,
            });
          },
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t(
                "cms.selection.section.importImageForm.error.importSuccess",
              ),
              variant: "success",
              duration: 1000,
            });
            onSuccess?.();
          },
        },
      );
      return;
    }

    await importImage(
      {
        imageId: mediaId,
        caption: submittedData.caption,
      },
      {
        onError: (error) => {
          console.error("Error", error);
          toast({
            title: t("error"),
            description: t(
              "cms.selection.section.importImageForm.error.importFailed",
            ),
            variant: "error",
            duration: 1000,
          });
        },
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t(
              "cms.selection.section.importImageForm.error.importSuccess",
            ),
            variant: "success",
            duration: 1000,
          });
          onSuccess?.();
          form.reset();
          setPendingMedia(undefined);
        },
      },
    );
  };

  const handleMediaSuccess = async (attachment: Attachment) => {
    if (!attachment.id) return;
    setPendingMedia(attachment);

    if (selection?.item.url && sectionId) {
      await editSelection({
        importDto: {
          imageId: attachment.id,
          caption: form.getValues("caption"),
        },
      });
      onSuccess?.();
    }
  };

  return (
    <Form {...form}>
      <form
        id="importImage"
        onSubmit={(e) => {
          e.stopPropagation();
          form.handleSubmit(onSubmit)(e);
        }}
        className="flex flex-col justify-between gap-5"
      >
        <div className="flex flex-col gap-5">
          {selection?.item.url && !pendingMedia ? (
            <div className="relative w-fit min-w-[200px] hover:cursor-pointer">
              <MediaPreview
                attachment={previewAttachment}
                previewImageUrl={selection.item.url}
                className="max-h-[350px] w-auto mb-0 mt-0"
              />
              <div
                className="absolute inset-0 bottom-0 left-0 gap-1.5 bg-black bg-opacity-70 opacity-0 hover:opacity-100 duration-300"
                onClick={onDelete}
              >
                <div className="flex flex-row justify-center items-center w-full h-full">
                  <Trash2 color="#dc2626" size={20} />
                  <span className="font-semibold text-base text-red-600">
                    {isVideoMedia
                      ? vt(
                          "cms.selection.section.artworks.editImageForm.deleteVideo",
                          "Supprimer la vidéo",
                        )
                      : t("cms.selection.section.artworks.editImageForm.delete")}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <CroppedImageInput
            hidePreview={Boolean(selection?.item.url && !pendingMedia)}
            label={t("cms.selection.section.artworks.importNewImageForm.media")}
            attachment={displayAttachment}
            imageURL={displayPreviewUrl}
            refetch={() => {}}
            imagePosition="sectionImportedImage"
            docId=""
            aspect={16 / 9}
            onSuccess={handleMediaSuccess}
            buildImageName={() =>
              generateImageName({
                primaryType: "SectionImportedImage",
                sectionTitle: sectionName,
              })
            }
          />

          <MultilingualField
            render={(lang) => <ImportImageForm lang={lang} />}
            fixed={getMainLanguage()}
          />
          <MultilingualField
            render={(lang) => <ImportImageForm lang={lang} />}
            excludeLang={getMainLanguage()}
          />
        </div>
        <div className="flex flex-col">
          <LoaderButton
            type="submit"
            form="importImage"
            disabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
          >
            {selection?.item.url
              ? t("cms.selection.section.artworks.editImageForm.button")
              : t("cms.selection.section.artworks.importNewImageForm.button")}
          </LoaderButton>
        </div>
      </form>
    </Form>
  );
};
